import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Brand_Enum, Business_Profile_Enum, Company, Company_Bool_Exp, Contract_History_Bool_Exp, Order, Order_Bool_Exp, Order_Status_Enum, Staff_User, Uuid_Comparison_Exp } from '@kheops/graphql';
import { Maybe } from 'graphql/jsutils/Maybe';
import { DisplayPhoto } from '../common/common';
import { OrdersCountByCategoryMap } from '../orders/list/OrderList';
import { useGetOrdersQuery } from '../queries/__generated__/getOrders.generated';
import { currentContextAtom } from '../state';

export type OrderPreview = Pick<Order, 'id' | 'creator_id' | 'created_at' | 'status' | 'reference_id' | 'suggestor_id' | 'friendly_id'>
& {
  contract: {
    id: string;
    is_trusted_supplier: boolean;
    company: Pick<Company, 'id' | 'tradeName' | 'business_profile' | 'brand'>
    & {
      photos: DisplayPhoto[]
    };
  };
  total_price_to_pay_excl_tax: number;
  total_price_to_pay_incl_tax: number;
  suggestor?: Maybe<{ staff_user?: Maybe<Pick<Staff_User, 'role'>> }>;
};

interface UseOrdersResult {
  orders: OrderPreview[];
  countMap: OrdersCountByCategoryMap;
  loading: boolean;
}

export interface OrdersFilters {
  contractFilter?: Contract_History_Bool_Exp;
  creatorFilter?: Uuid_Comparison_Exp;
  statusesToGet?: Order_Status_Enum[];
  aggregateInProgressCategoryStatuses: Order_Status_Enum[];
  aggregateValidationCategoryStatuses: Order_Status_Enum[];
  aggregateDeliveryCategoryStatuses: Order_Status_Enum[];
  aggregatePaymentCategoryStatuses: Order_Status_Enum[];
  aggregateCompletedCategoryStatuses: Order_Status_Enum[];
  myClientsFilter?: boolean;
  searchFilter: Order_Bool_Exp;
}

export const DISPUTE_STATUSES: Order_Status_Enum[] = [Order_Status_Enum.ClosedButContestedPayment]; 


export default function useOrders(
  offset: number,
  limit: number,
  queryFilters: OrdersFilters,
  currentUserId: string,
  onlyActionNeededStatuses: Order_Status_Enum[],
): UseOrdersResult {
  const context = useAtomValue(currentContextAtom);
  const [countMap, setCountMap] = useState<OrdersCountByCategoryMap>({
    IN_PROGRESS: 0,
    VALIDATION: 0,
    DELIVERY: 0,
    PAYMENT: 0,
    COMPLETED: 0,
    ALL: 0,
    CREATED_BY_ME: 0,
    WITH_ACTION_NEEDED: 0,
    MY_CLIENTS: 0,
    DISPUTE: 0,
  });

  const hasSomeBrands = context.userBrands?.length && !context.userBrands[0].has_all_brands;
  const hasSomeAreas = context.userAdministrativeAreas?.length && !context.userAdministrativeAreas[0].has_all_areas;

  const myClientsContractFilter: Company_Bool_Exp | undefined = hasSomeBrands || hasSomeAreas
    ? { _and: [] }
    : undefined;

  if (hasSomeBrands) {
    myClientsContractFilter!._and!.push({
      brand: {
        _in: context.userBrands!.map((brandData) => brandData.brand as Brand_Enum),
      },
    });
  }

  if (hasSomeAreas) {
    myClientsContractFilter!._and!.push({
      addresses: {
        administrativeAreaLevel2: {
          _in: context.userAdministrativeAreas!.map((areasData) => areasData.area!),
        },
      },
    });
  }

  const baseContractFilterExp: Contract_History_Bool_Exp = {
    buyingCompanyId: context.realm === Business_Profile_Enum.Buyer ? { _eq: context.companyId } : undefined,
    supplyingCompanyId: context.realm === Business_Profile_Enum.Supplier ? { _eq: context.companyId } : undefined,
    buying_company: queryFilters.myClientsFilter ? myClientsContractFilter : undefined,
  };

  const myClientsContractFilterExp: Contract_History_Bool_Exp = {
    buyingCompanyId: context.realm === Business_Profile_Enum.Buyer ? { _eq: context.companyId } : undefined,
    supplyingCompanyId: context.realm === Business_Profile_Enum.Supplier ? { _eq: context.companyId } : undefined,
    buying_company: myClientsContractFilter,
  };

  const { contractFilter, searchFilter, ...otherQueryFilters } = queryFilters;
  const { data, loading } = useGetOrdersQuery(
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        offset,
        limit,
        contractFilterExp: {
          ...baseContractFilterExp,
          ...contractFilter,
        },
        searchFilter: searchFilter ?? {},
        ...otherQueryFilters,
        currentUserId,
        aggregateDisputeStatuses: DISPUTE_STATUSES,
        onlyActionNeededStatuses,
        myClientsContractFilterExp,
      },
    },
  );

  useEffect(() => {
    if (data) {
      setCountMap({
        IN_PROGRESS: data.orders_in_progress_count.aggregate?.count || 0,
        VALIDATION: data.orders_in_validation_count.aggregate?.count || 0,
        PAYMENT: data.orders_in_payment_count.aggregate?.count || 0,
        DELIVERY: data.orders_in_delivery_count.aggregate?.count || 0,
        COMPLETED: data.orders_completed_count.aggregate?.count || 0,
        ALL: (data.orders_completed_count.aggregate?.count || 0) + (data.orders_in_progress_count.aggregate?.count || 0),
        CREATED_BY_ME: data.orders_created_by_me_count.aggregate?.count || 0,
        WITH_ACTION_NEEDED: data.orders_with_action_needed.aggregate?.count || 0,
        MY_CLIENTS: data.orders_my_clients.aggregate?.count || 0,
        DISPUTE: data.orders_dispute.aggregate?.count || 0,
      });
    }
  }, [data]);

  const results = useMemo(() => {
    return {
      orders: data?.order.map((order) => (
        {
          ...order,
          total_price_to_pay_excl_tax: order.total_price_to_pay_excl_tax as number,
          total_price_to_pay_incl_tax: order.total_price_to_pay_incl_tax as number,
          contract: {
            id: order.contract.id,
            company: context.realm === Business_Profile_Enum.Buyer ? order.contract.supplying_company : order.contract.buying_company,
            is_trusted_supplier: order.contract.is_trusted_supplier,
          },
        }
      )) || [],
      loading,
      countMap,
    };
  }, [data, loading, countMap]);

  return results;
}
